"use client";

import { useRef } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";
import { Button } from "@heroui/react";
import {
  ArrowRight,
  ArrowUp,
  Command,
  MessageCircleQuestion,
  Send,
  Sparkles,
  Zap,
} from "lucide-react";
import ScrollReveal from "./ScrollReveal";
import { SCHEDULE_DEMO_URL } from "@/lib/site-config";

const EASE = [0.25, 0.1, 0.25, 1] as const;

const bullets = [
  {
    icon: MessageCircleQuestion,
    title: "Respuestas al instante",
    description: "El estado de cualquier contratista o documento, preguntando.",
  },
  {
    icon: Zap,
    title: "Acciones reales",
    description: "Envía recordatorios de renovación y genera reportes por ti.",
  },
  {
    icon: Command,
    title: "Siempre a una tecla",
    description: "Un atajo desde cualquier pantalla del panel, con tu contexto.",
  },
];

/**
 * Veri showcase — light section consistent with the page; the copilot's dark
 * identity lives only in the floating chat window (echoing the product's dark
 * copilot surface), elevated over a soft brand glow.
 */
export default function CopilotSection() {
  return (
    <section
      id="copilot"
      aria-label="Veri, el copiloto con IA de Verify"
      className="overflow-hidden px-6 pb-16 pt-16 sm:pb-20 sm:pt-20"
    >
      <div className="relative mx-auto grid max-w-[74rem] grid-cols-1 items-center gap-12 lg:grid-cols-[1fr_1.05fr] lg:gap-20">
        {/* Copy */}
        <div className="mx-auto max-w-[540px] lg:mx-0">
          <ScrollReveal>
            <span className="mb-3 inline-flex items-center gap-2 rounded-full px-0.5 text-sm font-semibold text-accent sm:text-base">
              <Sparkles className="h-4 w-4" strokeWidth={2} />
              Copiloto con IA
            </span>
          </ScrollReveal>
          <ScrollReveal delay={0.08}>
            <h2 className="mb-4 text-balance text-[2rem] font-medium leading-[1.08] tracking-[-0.02em] text-dark-teal sm:text-[36px] lg:text-[44px]">
              Conoce a Veri.
              <br />
              <span className="bg-linear-to-r from-[#4A66D9] to-[#3F8E9D] bg-clip-text text-transparent">
                Pregúntale a tu operación.
              </span>
            </h2>
          </ScrollReveal>
          <ScrollReveal delay={0.14}>
            <p className="mb-8 max-w-[440px] text-pretty text-[15px] leading-[1.6] text-dark-teal/55 sm:text-[16px]">
              Veri vive dentro de Verify, entiende tu cumplimiento y responde
              con datos reales de tu operación, no con generalidades.
            </p>
          </ScrollReveal>

          <div className="flex flex-col gap-6">
            {bullets.map((b, i) => (
              <ScrollReveal key={b.title} delay={0.18 + i * 0.07}>
                <div className="flex items-start gap-4">
                  <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-accent/[0.08] ring-1 ring-accent/10">
                    <b.icon className="h-[18px] w-[18px] text-accent" strokeWidth={1.8} />
                  </span>
                  <div className="min-w-0">
                    <p className="text-[15px] font-semibold leading-snug text-dark-teal">
                      {b.title}
                    </p>
                    <p className="mt-1 text-[14px] leading-[1.55] text-dark-teal/55">
                      {b.description}
                    </p>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>

          <ScrollReveal delay={0.4}>
            <Button
              onPress={() => {
                window.open(SCHEDULE_DEMO_URL, "_blank", "noopener,noreferrer");
              }}
              className="btn-glow mt-9 flex h-11 items-center gap-2.5 rounded-2xl bg-accent px-6 text-[14px] font-semibold tracking-[-0.01em] text-white hover:bg-[#5570e0]"
            >
              Ver a Veri en acción
              <ArrowRight className="h-4 w-4 text-white/80" strokeWidth={2} />
            </Button>
          </ScrollReveal>
        </div>

        {/* Floating chat window */}
        <div className="relative isolate">
          {/* Brand glow behind the window */}
          <div
            className="pointer-events-none absolute -inset-10 -z-10"
            style={{
              background:
                "radial-gradient(ellipse 55% 50% at 45% 45%, rgba(101,129,234,0.18) 0%, transparent 70%)",
            }}
          />
          <div
            className="pointer-events-none absolute -bottom-12 -right-8 -z-10 h-64 w-64 rounded-full opacity-60 blur-3xl"
            style={{ background: "rgba(103,180,193,0.25)" }}
          />
          <ChatDemo />
        </div>
      </div>
    </section>
  );
}

function ChatDemo() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-120px" });
  const reduce = useReducedMotion();

  const show = (delay: number) =>
    reduce
      ? { initial: { opacity: 1, y: 0 }, animate: { opacity: 1, y: 0 } }
      : {
          initial: { opacity: 0, y: 12 },
          animate: inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 12 },
          transition: { duration: 0.45, delay, ease: EASE },
        };

  return (
    <motion.div
      ref={ref}
      initial={reduce ? false : { opacity: 0, y: 24, scale: 0.98 }}
      animate={
        reduce
          ? undefined
          : inView
            ? { opacity: 1, y: 0, scale: 1 }
            : { opacity: 0, y: 24, scale: 0.98 }
      }
      transition={{ duration: 0.7, ease: EASE }}
    >
      <div className="relative w-full overflow-hidden rounded-[20px] bg-[#0f1c30] p-4 ring-1 ring-white/10 shadow-[0_48px_100px_-36px_rgba(21,41,47,0.55)] sm:p-5">
        {/* Hairline top light */}
        <div className="pointer-events-none absolute inset-x-12 top-0 h-px bg-linear-to-r from-transparent via-white/25 to-transparent" />

        {/* Chat header */}
        <div className="mb-3.5 flex items-center justify-between border-b border-white/[0.07] pb-3">
          <div className="flex items-center gap-2">
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-linear-to-br from-[#6581EA] to-[#67B4C1]">
              <Sparkles className="h-3.5 w-3.5 text-white" strokeWidth={2} />
            </span>
            <div className="leading-tight">
              <p className="text-[12.5px] font-semibold text-white">Veri</p>
              <p className="text-[9.5px] text-emerald-400/90">● En línea</p>
            </div>
          </div>
          <span className="rounded-md border border-white/10 bg-white/[0.05] px-1.5 py-0.5 text-[10px] font-medium text-white/40">
            ⌘J
          </span>
        </div>

        {/* Messages */}
        <div className="space-y-2.5">
          <motion.div
            {...show(0.15)}
            className="ml-auto w-fit max-w-[85%] rounded-2xl rounded-br-md bg-white/10 px-3.5 py-2 text-[12.5px] leading-relaxed text-white/90"
          >
            ¿Qué contratistas tienen documentos por vencer esta semana?
          </motion.div>

          <motion.div {...show(0.7)} className="w-fit max-w-[92%]">
            <div className="rounded-2xl rounded-bl-md bg-[#6581EA]/20 px-3.5 py-2 text-[12.5px] leading-relaxed text-white/90 ring-1 ring-[#6581EA]/25">
              Esta semana vencen{" "}
              <strong className="text-white">3 documentos</strong> de 2
              contratistas. ¿Envío los recordatorios de renovación?
            </div>
          </motion.div>

          <motion.div {...show(1.1)} className="space-y-1.5 pl-1">
            <ResultRow
              name="Rivas Construcciones SpA"
              detail="Póliza de seguro · vence en 3 días"
              tone="bg-red-400"
            />
            <ResultRow
              name="Rivas Construcciones SpA"
              detail="Certificado F30 · vence en 5 días"
              tone="bg-amber-400"
            />
            <ResultRow
              name="Andes Mining Services"
              detail="Adhesión a mutualidad · vence en 6 días"
              tone="bg-amber-400"
            />
          </motion.div>

          {/* Quick replies — read as tappable actions, not messages */}
          <motion.div {...show(1.5)} className="flex flex-wrap justify-end gap-2 pt-1.5">
            <span className="inline-flex items-center rounded-full border border-white/10 bg-white/[0.04] px-3.5 py-1.5 text-[11.5px] font-medium text-white/55">
              Ver detalle
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#6581EA] px-3.5 py-1.5 text-[11.5px] font-semibold text-white shadow-[0_8px_20px_-8px_rgba(101,129,234,0.9)]">
              <Send className="h-3 w-3" strokeWidth={2} />
              Enviar recordatorios
            </span>
          </motion.div>
        </div>

        {/* Composer */}
        <motion.div
          {...show(1.7)}
          className="mt-4 flex items-center gap-2.5 border-t border-white/[0.07] pt-3.5"
        >
          <div className="flex flex-1 items-center rounded-xl bg-white/[0.06] px-3.5 py-2.5 ring-1 ring-white/10">
            <span className="flex-1 truncate text-[12px] text-white/35">
              Pregúntale a Veri sobre tu operación…
            </span>
          </div>
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#6581EA] shadow-[0_8px_20px_-8px_rgba(101,129,234,0.9)]">
            <ArrowUp className="h-4 w-4 text-white" strokeWidth={2.2} />
          </span>
        </motion.div>
      </div>
    </motion.div>
  );
}

function ResultRow({
  name,
  detail,
  tone,
}: {
  name: string;
  detail: string;
  tone: string;
}) {
  return (
    <div className="flex items-center gap-2.5 rounded-xl border border-white/[0.07] bg-white/[0.04] px-3 py-1.5">
      <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${tone}`} />
      <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 leading-tight">
        <p className="truncate text-[12px] font-semibold text-white/90">
          {name}
        </p>
        <p className="truncate text-[11px] text-white/45">{detail}</p>
      </div>
    </div>
  );
}
